import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { wsManuscriptEventUrl } from "utils/urls";

export function useManuscriptEvents({
    spaceUuid,
    manuscriptId,
    enabled = true,
    isOTConnected = true,
    requiresOTConnection = false,
    onManuscriptChanged,
}) {
    const wsRef = useRef(null);
    const onManuscriptChangedRef = useRef(onManuscriptChanged);
    const [isBrowserOnline, setIsBrowserOnline] = useState(() => typeof navigator === "undefined" || navigator.onLine);
    const [isEventConnected, setIsEventConnected] = useState(false);

    useEffect(() => {
        onManuscriptChangedRef.current = onManuscriptChanged;
    }, [onManuscriptChanged]);

    const connectionReady = useMemo(() => enabled && !!spaceUuid && !!manuscriptId, [enabled, manuscriptId, spaceUuid]);

    useEffect(() => {
        const handleOnline = () => setIsBrowserOnline(true);
        const handleOffline = () => setIsBrowserOnline(false);

        window.addEventListener("online", handleOnline);
        window.addEventListener("offline", handleOffline);

        return () => {
            window.removeEventListener("online", handleOnline);
            window.removeEventListener("offline", handleOffline);
        };
    }, []);

    const handleMessage = useCallback((event) => {
        const data = JSON.parse(event.data || "{}");
        if (data?.type !== "manuscript_changed") {
            return;
        }
        onManuscriptChangedRef.current?.(data.payload || {});
    }, []);

    useEffect(() => {
        if (!connectionReady) {
            setIsEventConnected(false);
            return undefined;
        }

        let reconnectTimerId = null;
        let closedByEffect = false;

        const connect = () => {
            const socket = new WebSocket(wsManuscriptEventUrl(spaceUuid, manuscriptId));
            wsRef.current = socket;

            socket.onopen = () => {
                setIsEventConnected(true);
            };
            socket.onmessage = handleMessage;
            socket.onerror = () => {
                setIsEventConnected(false);
            };
            socket.onclose = () => {
                if (wsRef.current === socket) {
                    wsRef.current = null;
                }
                setIsEventConnected(false);

                if (!closedByEffect) {
                    reconnectTimerId = window.setTimeout(connect, 1000);
                }
            };
        };

        connect();

        return () => {
            closedByEffect = true;
            setIsEventConnected(false);
            if (reconnectTimerId) {
                window.clearTimeout(reconnectTimerId);
            }
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
        };
    }, [connectionReady, handleMessage, manuscriptId, spaceUuid]);

    return {
        isConnected: isBrowserOnline && isEventConnected && (!requiresOTConnection || Boolean(isOTConnected)),
        isBrowserOnline,
        isEventConnected,
    };
}

export default useManuscriptEvents;
